import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	ContainerDispatchModelConverter as ModelConverter, ContainerDispatchStoreModel,
	ContainerDispatchStoreModel as StoreModel,
	ContainerDispatchViewModel as ViewModel,
	CreatableContainerDispatchViewModel as CreatableViewModel
} from 'services/nuclide/domain/model/ContainerDispatchModel';
import { DebugConsole } from '../../../lib/debug/DebugConsole';

import { PropellerError } from '../../../lib/persistence/http/error/PropellerError';
import { ModelQueryOrderClosureBuilder } from '../../../lib/persistence/idb/query/ModelQueryOrderClosureBuilder';
import { AsyncFetchStatus } from '../../../store/common/AsyncFetchStatus';
import { checkFetchStatus } from '../../../store/common/AsyncFetchStatus.util';
import { AsyncReducerStatus } from '../../../store/common/AsyncReducerStatus';
import { RootState } from '../../../store/store';
import { ContainerDispatchHttpPersistence as HttpPersistence } from '../persistence/ContainerDispatchHttpPersistence';
import { ContainerDispatchPersistence as Persistence } from '../persistence/ContainerDispatchPersistence';

// Declare a container dispatch state type
export interface ContainerDispatchState {
	containerDispatch: Array<StoreModel>;
	createdContainerDispatch: StoreModel | null;
	fetchStatus: AsyncFetchStatus;
	lastFetchError: Error | PropellerError | null;
	actionStatus: AsyncReducerStatus;
	lastActionError: Error | PropellerError | null;
}

// The initial state
const initialState  = {
	containerDispatch: [] as Array<StoreModel>,
	createdContainerDispatch: null,
	fetchStatus: AsyncFetchStatus.INITIAL,
	lastFetchError: null,
	actionStatus: AsyncReducerStatus.IDLE,
	lastActionError: null
} as ContainerDispatchState;

// Implementation of the async actions
export const fetchContainerDispatch = createAsyncThunk(
	'container-dispatch/fetch-entity',
	async (params: { clientUuid: string, facilityUuid: string, dispatchUuid: string }): Promise<ContainerDispatchStoreModel> => {
		const persistence = new HttpPersistence(params.clientUuid, params.facilityUuid);
		return persistence.fetch(params.dispatchUuid);
	},
	{
		condition: (params, { getState }): boolean => {
			// Silently abort the action
			const { containerDispatch } = getState() as RootState;
			return checkFetchStatus(containerDispatch.fetchStatus);
		}
	}
);

export const fetchContainerDispatches = createAsyncThunk(
	'container-dispatch/fetch-collection',
	async (params: { clientUuid: string, facilityUuid: string }): Promise<Array<ContainerDispatchStoreModel | null>> => {
		const persistence = new HttpPersistence(params.clientUuid, params.facilityUuid);
		return persistence.fetchCollection();
	},
	{
		condition: (params, { getState }): boolean => {
			// Silently abort the action
			const { containerDispatch } = getState() as RootState;
			return checkFetchStatus(containerDispatch.fetchStatus);
		}
	}
);

export const resetContainerDispatch = createAsyncThunk(
	'container-dispatch/reset',
	async (params: { clientUuid: string; facilityUuid: string;  }): Promise<Array<StoreModel>> => {
		const persistence = new Persistence(params.clientUuid, params.facilityUuid);
		return persistence.resetCollection();
	},
	{
		condition: (params, { getState }): boolean => {
			// Sliently abort the action
			const { containerDispatch } = getState() as RootState;
			return checkFetchStatus(containerDispatch.fetchStatus);
		}
	}
);

export const createContainerDispatch = createAsyncThunk(
	'container-dispatch/create',
	async (viewModel: CreatableViewModel): Promise<StoreModel> => {
		const persistence = new Persistence(viewModel.Client, viewModel.Facility);
		return persistence.createEntity(viewModel);
	},
	{
		condition: (params, { getState }): boolean => {
			// Abort the action with an error
			const { containerDispatch } = getState() as RootState;
			if (containerDispatch.actionStatus !== AsyncReducerStatus.IDLE) {
				throw new Error('Action not available');
			}
			return true;
		}
	}
);

export const deleteContainerDispatch = createAsyncThunk(
	'container-dispatch/delete',
	async (viewModel: ViewModel): Promise<StoreModel> => {
		const persistence = new Persistence(viewModel.Client, viewModel.Facility);
		return persistence.deleteEntity(viewModel);
	},
	{
		condition: (params, { getState }): boolean => {

			// Abort the action with an error
			const { containerDispatch } = getState() as RootState;
			if (containerDispatch.actionStatus !== AsyncReducerStatus.IDLE) {
				throw new Error('Action not available');
			}
			return true;
		}
	}
);

// Slice definition
export const containerDispatchSlice = createSlice({
	name: 'containerDispatch',
	initialState,
	// Regular syncronous reducers
	reducers: {
		resetState(state) {
			Object.assign(state, initialState);
		},
		resetActionStatus(state) {
			state.actionStatus = AsyncReducerStatus.IDLE;
		}
	},
	extraReducers: {
		[String(fetchContainerDispatch.pending)]: (state) => {
			if (state.fetchStatus === AsyncFetchStatus.INITIAL) {
				state.fetchStatus = AsyncFetchStatus.INITIAL_PENDIG;
			} else {
				state.fetchStatus = AsyncFetchStatus.PENDING;
			}
		},
		[String(fetchContainerDispatch.fulfilled)]: (state, action: PayloadAction<Array<StoreModel>>) => {
			state.containerDispatch = action.payload;
			state.fetchStatus = AsyncFetchStatus.SUCCESS;
		},
		[String(fetchContainerDispatch.rejected)]: (state, action) => {
			state.fetchStatus = AsyncFetchStatus.FAILED;
			state.lastFetchError = action.error;
		},
		[String(fetchContainerDispatches.pending)]: (state) => {
			if (state.fetchStatus === AsyncFetchStatus.INITIAL) {
				state.fetchStatus = AsyncFetchStatus.INITIAL_PENDIG;
			} else {
				state.fetchStatus = AsyncFetchStatus.PENDING;
			}
		},
		[String(fetchContainerDispatches.fulfilled)]: (state, action: PayloadAction<Array<StoreModel>>) => {
			state.containerDispatch = action.payload;
			state.fetchStatus = AsyncFetchStatus.SUCCESS;
		},
		[String(fetchContainerDispatches.rejected)]: (state, action) => {
			state.fetchStatus = AsyncFetchStatus.FAILED;
			state.lastFetchError = action.error;
		},
		[String(resetContainerDispatch.pending)]: (state) => {
			if (state.fetchStatus === AsyncFetchStatus.INITIAL) {
				state.fetchStatus = AsyncFetchStatus.INITIAL_PENDIG;
			} else {
				state.fetchStatus = AsyncFetchStatus.PENDING;
			}
		},
		[String(resetContainerDispatch.fulfilled)]: (state, action: PayloadAction<Array<StoreModel>>) => {
			state.containerDispatch = action.payload;
			state.fetchStatus = AsyncFetchStatus.SUCCESS;
		},
		[String(resetContainerDispatch.rejected)]: (state, action) => {
			state.fetchStatus = AsyncFetchStatus.FAILED;
			state.lastFetchError = action.error;
		},
		[String(createContainerDispatch.pending)]: (state) => {
			state.actionStatus = AsyncReducerStatus.CREATE_PENDING;
			state.createdContainerDispatch = null;
		},
		[String(createContainerDispatch.fulfilled)]: (state, action: PayloadAction<StoreModel>) => {
			state.containerDispatch.push(action.payload);
			state.containerDispatch = state.containerDispatch.sort(new ModelQueryOrderClosureBuilder<StoreModel>().build('Container'));
			state.createdContainerDispatch = action.payload;
			state.actionStatus = AsyncReducerStatus.CREATED;
		},
		[String(createContainerDispatch.rejected)]: (state, action) => {
			state.lastActionError = action.error;
			state.actionStatus = AsyncReducerStatus.FAILED;
		},
		[String(deleteContainerDispatch.pending)]: (state) => {
			state.actionStatus = AsyncReducerStatus.DELETE_PENDING;
		},
		[String(deleteContainerDispatch.fulfilled)]: (state, action: PayloadAction<StoreModel>) => {
			const index = state.containerDispatch.findIndex((entry: { Uuid: string; }): boolean => {
				return entry.Uuid === action.payload.Uuid;
			}) ?? null;
			if (index !== null) {
				state.containerDispatch.splice(index, 1);
			}
			state.actionStatus = AsyncReducerStatus.DELETED;
		},
		[String(deleteContainerDispatch.rejected)]: (state, action) => {
			state.actionStatus = AsyncReducerStatus.FAILED;
			state.lastActionError = action.error.message;
		}
	}
});

export const { resetState, resetActionStatus } = containerDispatchSlice.actions;

// Export the reducer as default
export default containerDispatchSlice.reducer;

export const selectContainerDispatchByUuid = (uuid: string): (rootState: RootState) => ViewModel | null => {
	return (rootState: RootState): ViewModel | null => {
		const storeModel = rootState.containerDispatch.containerDispatch.find((sModel): boolean => {
			return sModel.Uuid === uuid;
		}) ?? null;
		try {
			return new ModelConverter().fromStoreModel(storeModel).toViewModel();
		} catch (error) {
			DebugConsole.error(error);
			return null;
		}
	};
};

export const selectCreatedContainerDispatch = (): (rootState: RootState) => ViewModel | null => {
	return (rootState: RootState): ViewModel | null => {
		const storeModel = rootState.containerDispatch.createdContainerDispatch;
		if (storeModel === null) {
			return null;
		}
		try {
			return new ModelConverter().fromStoreModel(storeModel).toViewModel();
		} catch (error) {
			DebugConsole.error(error);
			return null;
		}
	};
};
